<!-- src/components/admin/UnassignedDevices.vue
     Cartes provisionnées en atelier et pas encore rattachées à un client.

     Une carte est fabriquée avant d'être vendue : elle porte son jeton et elle publie, mais
     elle n'appartient à personne — et comme `devices.client_id` est NOT NULL, elle ne peut pas
     figurer dans la supervision. Sans cette file, elle parlerait sans que rien ne l'écoute. -->
<template>
  <div v-if="devices.length" class="bg-white shadow rounded-lg mb-8 overflow-hidden">
    <div class="px-4 py-4 sm:px-6 border-b border-gray-200 flex items-center justify-between">
      <div>
        <h3 class="text-lg font-medium text-gray-900">
          Cartes à rattacher
          <span class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            {{ devices.length }}
          </span>
        </h3>
        <p class="mt-1 text-sm text-gray-500">
          Provisionnées en atelier. Tant qu'elles ne sont rattachées à aucun client, leurs
          remontées ne sont pas enregistrées.
        </p>
      </div>
      <div class="flex items-center gap-4">
        <button class="text-sm text-primary-600 hover:text-primary-500" @click="loadSignupQr">
          QR du guide de démarrage
        </button>
        <button class="text-sm text-primary-600 hover:text-primary-500" @click="refresh">Actualiser</button>
      </div>
    </div>

    <!-- Le QR de création de compte : le même sur tous les produits, aucun secret dedans. Il
         s'imprime UNE FOIS dans le guide de démarrage, et peut figurer à l'extérieur du colis
         — contrairement au code de rattachement, qui doit rester à l'intérieur. -->
    <div v-if="signup" class="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50 zone-impression">
      <div class="flex flex-wrap items-center gap-6">
        <img :src="signup.qr" alt="QR de création de compte" class="w-36 h-36 bg-white border border-gray-200 rounded" />
        <div>
          <p class="text-sm font-medium text-gray-900">Créez votre compte</p>
          <p class="mt-1 text-sm text-gray-600 max-w-sm">
            Scannez ce code, ou rendez-vous sur
            <span class="font-mono">{{ signup.url }}</span>.
            Vous rattacherez ensuite votre transmetteur avec le code reçu séparément.
          </p>
          <div class="mt-3 flex gap-3 print:hidden">
            <button class="px-3 py-1.5 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900"
                    @click="print()">Imprimer</button>
            <button class="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                    @click="signup = null">Fermer</button>
          </div>
        </div>
      </div>
    </div>

    <ul class="divide-y divide-gray-200">
      <li v-for="d in devices" :key="d.imei" class="px-4 py-4 sm:px-6">
        <div class="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p class="font-mono text-sm text-gray-900">{{ d.imei }}</p>
            <p class="mt-1 text-xs text-gray-500">
              <!-- « Vue » sépare la carte déjà sur le terrain de celle encore sur l'établi :
                   la première attend un client, la seconde attend d'être expédiée. -->
              <span v-if="d.vue" class="text-green-700">
                a émis {{ d.seen_count }} fois · dernière {{ formatDate(d.last_seen_at) }}
              </span>
              <span v-else class="text-gray-400">jamais émis depuis le provisionnement</span>
              · provisionnée {{ formatDate(d.token_created_at) }} par {{ d.key_label || '?' }}
              · schéma {{ d.topic_schema }}
            </p>
            <!-- Contrôle de la jauge AVANT expédition (note fw 2.10.6) : c'est ici, à l'atelier,
                 qu'une carte mal paramétrée doit se voir — pas chez le client. -->
            <p v-if="jaugeDe(d)" class="mt-1 text-xs"
               :class="jaugeDe(d).ton === 'alerte' ? 'text-red-600 font-medium' : (jaugeDe(d).ton === 'ok' ? 'text-green-700' : 'text-gray-500')">
              <span v-if="jaugeDe(d).ton === 'alerte'">⚠️ À corriger avant expédition — </span>Jauge : {{ jaugeDe(d).texte }}
              <span class="font-normal text-gray-400">({{ jaugeDe(d).detail }})</span>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <!-- Deux gestes distincts, et c'est volontaire : « Rattacher » est le raccourci
                 admin (on connaît déjà le client), « Code de livraison » est le parcours
                 normal — le client rattache lui-même, avec un secret qui voyage à part. -->
            <button
              class="px-3 py-1.5 text-sm rounded-md border border-primary-600 text-primary-700 hover:bg-primary-50"
              @click="openCode === d.imei ? closeCode() : openCode = d.imei">
              {{ openCode === d.imei ? 'Annuler' : 'Code de livraison' }}
            </button>
            <button
              class="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
              @click="open === d.imei ? open = null : open = d.imei">
              {{ open === d.imei ? 'Annuler' : 'Rattacher' }}
            </button>
          </div>
        </div>

        <!-- Émission du code : l'adresse est facultative. Sans elle, le code part sur papier
             (étiquette imprimable) ; avec elle, il partira AUSSI par courriel — les deux
             canaux portent le QR. -->
        <form v-if="openCode === d.imei && !issued" class="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
              @submit.prevent="issue(d)">
          <div class="sm:col-span-2">
            <label class="block text-xs font-medium text-gray-600 mb-1">Envoyer aussi le code par courriel à l'acheteur</label>
            <input v-model="codeForm.email" type="email" placeholder="adresse@exemple.fr — laisser vide pour une remise papier seule"
                   class="w-full rounded-md border-gray-300 text-sm" />
          </div>
          <button type="submit" :disabled="busy"
                  class="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
            {{ busy ? 'Émission…' : 'Émettre le code' }}
          </button>
          <p class="sm:col-span-3 text-xs text-gray-500">
            Le code s'affichera <strong>une seule fois</strong> : le serveur n'en garde qu'une
            empreinte et sera incapable de le réafficher. En émettre un nouveau invalide le précédent.
          </p>
        </form>

        <form v-if="open === d.imei" class="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3" @submit.prevent="attach(d)">
          <select v-model="form.client_id" required class="rounded-md border-gray-300 text-sm">
            <option value="" disabled>Client…</option>
            <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
          <input v-model="form.name" required placeholder="Nom de la carte" class="rounded-md border-gray-300 text-sm" />
          <input v-model="form.location" required placeholder="Emplacement" class="rounded-md border-gray-300 text-sm" />
          <button type="submit" :disabled="busy"
                  class="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            {{ busy ? 'Rattachement…' : 'Confirmer' }}
          </button>
        </form>
        <p v-if="error && (open === d.imei || openCode === d.imei)" class="mt-2 text-sm text-red-600">{{ error }}</p>

        <!-- L'étiquette à glisser dans le colis. Elle porte le QR ET le code en clair : le QR
             est le geste normal, le code tapé à la main est le filet quand un lecteur refuse. -->
        <div v-if="issued && issued.imei === d.imei"
             class="zone-impression mt-4 border-2 border-dashed border-gray-300 rounded-lg p-5 bg-white">
          <p class="text-xs uppercase tracking-wide text-gray-500">Rattachement de votre transmetteur</p>
          <div class="mt-3 flex flex-wrap items-center gap-6">
            <img v-if="issued.qr" :src="issued.qr" alt="QR de rattachement"
                 class="w-40 h-40 border border-gray-200 rounded" />
            <div>
              <p class="font-mono text-2xl tracking-[0.25em] text-gray-900">{{ issued.code }}</p>
              <p class="mt-3 text-sm text-gray-700 max-w-sm">
                Scannez le QR avec votre téléphone, ou rendez-vous sur
                <span class="font-medium">{{ claimHost }}</span> et saisissez ce code dans
                « Ajouter un appareil ».
              </p>
              <p class="mt-2 text-xs text-gray-500">Ce code ne sert qu'une fois. Appareil …{{ d.imei.slice(-4) }}.</p>
            </div>
          </div>

          <div class="mt-4 flex items-center gap-3 print:hidden">
            <button class="px-3 py-1.5 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900"
                    @click="print()">Imprimer</button>
            <button class="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                    @click="copy()">{{ copied ? 'Copié' : 'Copier le code' }}</button>
            <button class="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                    @click="closeCode()">Fermer</button>
            <span v-if="issued.sent" class="text-xs text-green-700">Envoyé par courriel.</span>
            <span v-else class="text-xs text-amber-700">Non envoyé par courriel — remise papier.</span>
          </div>

          <!-- Envoyer après coup, tant que l'étiquette est ouverte : le code affiché est renvoyé
               au serveur, qui vérifie qu'il est bien le code en cours de la carte. -->
          <form class="mt-3 flex flex-wrap items-center gap-2 print:hidden" @submit.prevent="envoyerCode(d)">
            <input v-model="envoiEmail" type="email" required placeholder="Courriel de l'acheteur"
                   class="flex-1 min-w-[14rem] rounded-md border-gray-300 text-sm" />
            <button type="submit" :disabled="busy"
                    class="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
              {{ busy ? 'Envoi…' : 'Envoyer par courriel' }}
            </button>
            <span v-if="envoiOk" class="text-xs text-green-700">{{ envoiOk }}</span>
          </form>
          <p class="mt-3 text-xs text-red-600 print:hidden">
            Une fois cette étiquette fermée, le code est définitivement irrécupérable.
          </p>
        </div>
      </li>
    </ul>
  </div>
</template>

<!-- Style NON scopé, à dessein : une règle d'impression doit pouvoir masquer le reste de la
     page, ce qu'un sélecteur scopé ne peut pas faire. Le périmètre reste étroit — rien ne
     s'applique hors de @media print. -->
<style>
@media print {
  body * { visibility: hidden !important; }
  .zone-impression, .zone-impression * { visibility: visible !important; }
  .zone-impression { position: absolute; left: 0; top: 0; width: 100%; border: none !important; background: #fff !important; }
  .print\:hidden { display: none !important; }
}
</style>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import socketService from '@/services/socket.service';
import apiService from '@/services/api.service';
import { libelleJauge } from '@/utils/gauge';

export default defineComponent({
  name: 'UnassignedDevices',

  setup() {
    const store = useStore();
    const devices = ref([]);
    const clients = ref([]);
    const open    = ref(null);
    const busy    = ref(false);
    const error   = ref(null);
    const form     = ref({ client_id: '', name: '', location: '' });
    const openCode = ref(null);
    const codeForm = ref({ email: '' });
    const issued   = ref(null);      // { imei, code, qr, claim_url, sent } — jamais mis en store
    const copied   = ref(false);
    const signup   = ref(null);      // { url, qr } — le QR du guide, sans secret

    const refresh = async () => {
      try { devices.value = await store.dispatch('admin/fetchUnassignedDevices'); }
      catch (e) { /* l'erreur est déjà dans le store */ }
    };

    const attach = async (d) => {
      busy.value = true; error.value = null;
      try {
        await store.dispatch('admin/attachDevice', { imei: d.imei, ...form.value });
        open.value = null;
        form.value = { client_id: '', name: '', location: '' };
        await refresh();
      } catch (e) {
        error.value = e.response?.data?.message || 'Le rattachement a échoué.';
      } finally {
        busy.value = false;
      }
    };

    // Le code en clair ne vit QUE dans ce composant, et disparaît à la fermeture : il n'entre
    // ni dans le store, ni dans une URL, ni dans un journal.
    const issue = async (d) => {
      busy.value = true; error.value = null;
      try {
        issued.value = { imei: d.imei, ...(await store.dispatch('admin/issueClaimCode',
          { imei: d.imei, email: codeForm.value.email || null })) };
        codeForm.value = { email: '' };
      } catch (e) {
        error.value = e.response?.data?.message || 'L’émission du code a échoué.';
      } finally {
        busy.value = false;
      }
    };

    const loadSignupQr = async () => {
      if (signup.value) { signup.value = null; return; }
      try { signup.value = (await apiService.getSignupQr()).data; }
      catch (e) { error.value = 'QR de création de compte indisponible.'; }
    };

    const envoiEmail = ref('');
    const envoiOk    = ref('');
    const envoyerCode = async (d) => {
      busy.value = true; error.value = null; envoiOk.value = '';
      try {
        const { data } = await apiService.sendClaimCode(d.imei, issued.value.code, envoiEmail.value);
        issued.value.sent = true;
        envoiOk.value = data.message;
        envoiEmail.value = '';
      } catch (e) {
        error.value = e.response?.data?.message || 'L’envoi du courriel a échoué.';
      } finally {
        busy.value = false;
      }
    };

    const closeCode = () => { openCode.value = null; issued.value = null; copied.value = false; error.value = null;
                              envoiEmail.value = ''; envoiOk.value = ''; };
    const copy = async () => {
      try { await navigator.clipboard.writeText(issued.value.code); copied.value = true; }
      catch (e) { /* presse-papier refusé (contexte non sécurisé) : le code reste lisible */ }
    };
    // L'hôte affiché sur l'étiquette vient du lien renvoyé par le serveur, donc d'une seule
    // source de vérité (PUBLIC_BASE_URL) — pas d'une URL recopiée dans le frontend.
    const claimHost = computed(() => {
      try { return new URL(issued.value.claim_url).host; } catch (e) { return window.location.host; }
    });

    const formatDate = (v) => v ? new Date(v.includes('T') ? v : v.replace(' ', 'T') + 'Z')
      .toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'short', timeStyle: 'short' }) : '—';

    // Une carte qui se réveille doit apparaître sans qu'on recharge la page : c'est justement
    // au moment où elle parle qu'on veut savoir qu'elle attend un client.
    const onUnassigned = () => refresh();

    onMounted(async () => {
      await refresh();
      try { clients.value = await store.dispatch('admin/fetchClients'); } catch (e) { /* ignoré */ }
      socketService.getSocket()?.on('device:unassigned', onUnassigned);
    });
    onUnmounted(() => socketService.getSocket()?.off('device:unassigned', onUnassigned));

    return { devices, clients, open, busy, error, form, refresh, attach, formatDate,
             openCode, codeForm, issued, copied, issue, closeCode, copy, claimHost,
             envoiEmail, envoiOk, envoyerCode,
             signup, loadSignupQr, jaugeDe: d => libelleJauge(d.jauge),
             print: () => window.print() };
  }
});
</script>
